/**
 * /expert/payouts/setup — Stripe Connect onboarding (Phase A).
 *
 * Three states:
 *   not_started   → render the start-onboarding form (country picker + CTA)
 *   in_progress   → expert went to Stripe but hasn't completed KYC; show
 *                   status + "Resume onboarding" button + outstanding
 *                   requirements list
 *   complete      → payouts_enabled=true; show success state + link to
 *                   Stripe Express dashboard (hosted login link comes
 *                   later in Phase D)
 *
 * The page also handles a special `disabled` state when the backend's
 * STRIPE_CONNECT_ENABLED flag is off — renders a "coming soon" message.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ExternalLink, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MarketingNav from "@/components/MarketingNav";
import { API, useAuth } from "@/App";


export default function ExpertPayoutSetupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);            // { state, payouts_enabled, ... }
  const [countries, setCountries] = useState([]);        // [{ code, name }]
  const [selectedCountry, setSelectedCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Auth gate: redirect non-logged-in to login
  useEffect(() => {
    if (!user) {
      navigate("/login?return_to=/expert/payouts/setup");
    }
  }, [user, navigate]);

  // Initial load: pull status + supported countries in parallel
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [statusRes, countriesRes] = await Promise.all([
          axios.get(`${API}/expert/stripe-connect/status`),
          axios.get(`${API}/expert/stripe-connect/supported-countries`).catch(() => ({ data: { countries: [] } })),
        ]);
        if (cancelled) return;
        setStatus(statusRes.data);
        setCountries(countriesRes.data.countries || []);
      } catch (e) {
        if (cancelled) return;
        // 403 here usually means "not a verified expert yet" — the backend
        // returns a specific detail string we can surface verbatim.
        const detail = e?.response?.data?.detail || "Failed to load payout status.";
        setError(detail);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Show a one-shot toast if we're returning from Stripe (return_url query)
  useEffect(() => {
    const flag = searchParams.get("status");
    if (flag === "return") {
      toast.success("You're back from Stripe. We're syncing your account…");
    } else if (flag === "refresh") {
      toast.message("Onboarding link expired — generate a new one to continue.");
    }
  }, [searchParams]);

  const startOnboarding = async () => {
    if (!selectedCountry) {
      toast.error("Please select your country first.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/expert/stripe-connect/start`, {
        country: selectedCountry,
      });
      // Redirect immediately — the AccountLink is short-lived (minutes).
      window.location.assign(res.data.onboarding_url);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start onboarding.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  const refreshLink = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/expert/stripe-connect/refresh-link`);
      window.location.assign(res.data.onboarding_url);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not refresh onboarding link.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  const requirementsCount = useMemo(() => {
    const req = status?.requirements_due;
    if (!req) return 0;
    return (req.currently_due?.length || 0) + (req.past_due?.length || 0);
  }, [status]);

  // ─── Loading / Error / Auth states ────────────────────────────────────
  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="bg-white border border-warm-grey-200 rounded-lg p-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warm-grey-700 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-display text-xl mb-2">Can't set up payouts yet</h2>
              <p className="text-warm-grey-800">{error}</p>
              <Button
                variant="ghost"
                onClick={() => navigate("/expert-dashboard")}
                className="mt-4 -ml-3"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ─── State: disabled (feature flag off) ───────────────────────────────
  if (status?.state === "disabled") {
    return (
      <PageShell>
        <div className="bg-white border border-warm-grey-200 rounded-lg p-8">
          <h2 className="font-display text-2xl mb-3">Payouts setup — coming soon</h2>
          <p className="text-warm-grey-800 leading-relaxed">
            We're rolling out Stripe Connect to experts in stages. Your account
            is in the queue. In the meantime, payouts continue via the existing
            manual process — reach out to{" "}
            <a href="mailto:founder@xpert.company" className="text-brand-green underline">
              founder@xpert.company
            </a>{" "}
            if you have a pending payout.
          </p>
        </div>
      </PageShell>
    );
  }

  // ─── State: complete (payouts enabled) ────────────────────────────────
  if (status?.state === "complete") {
    return (
      <PageShell>
        <div className="bg-white border border-warm-grey-200 rounded-lg p-8">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-brand-green mt-1 shrink-0" />
            <div>
              <h2 className="font-display text-2xl mb-1">Payouts are set up</h2>
              <p className="text-warm-grey-700">
                You're ready to receive payouts for completed sessions.
                {status.country_display ? ` Country: ${status.country_display}.` : ""}
              </p>
            </div>
          </div>
          <div className="bg-paper border border-warm-grey-200 rounded p-4 text-sm text-warm-grey-800 leading-relaxed">
            <p className="mb-2"><strong>How payouts work:</strong></p>
            <ul className="space-y-1 list-disc pl-5">
              <li>Payouts are released by the founder after each completed session, in batches.</li>
              <li>Payouts are denominated in USD. If your bank account is in a non-USD currency, Stripe converts at its prevailing rate and may deduct an FX conversion fee.</li>
              <li>Tax reporting follows your local jurisdiction — you're responsible for declaring this income to your tax authority. Your Stripe Express dashboard has downloadable transaction history for your records.</li>
            </ul>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/expert-dashboard")}
            className="mt-6 -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
          </Button>
        </div>
      </PageShell>
    );
  }

  // ─── State: in_progress (KYC submitted but not yet enabled) ───────────
  if (status?.state === "in_progress") {
    return (
      <PageShell>
        <div className="bg-white border border-warm-grey-200 rounded-lg p-8">
          <h2 className="font-display text-2xl mb-3">Onboarding in progress</h2>
          <p className="text-warm-grey-800 mb-4 leading-relaxed">
            Stripe is reviewing the information you submitted. This usually
            takes a few minutes but can take up to 48 hours.
          </p>
          {requirementsCount > 0 && (
            <div className="bg-paper border border-warm-grey-200 rounded p-4 mb-4">
              <p className="text-sm font-semibold mb-2">Stripe needs more from you ({requirementsCount}):</p>
              <ul className="text-sm text-warm-grey-800 space-y-1 list-disc pl-5">
                {(status.requirements_due?.currently_due || []).map((r) => (
                  <li key={r}>{prettyRequirement(r)}</li>
                ))}
                {(status.requirements_due?.past_due || []).map((r) => (
                  <li key={`pd-${r}`} className="text-red-700">{prettyRequirement(r)} <span className="text-xs">(past due)</span></li>
                ))}
              </ul>
            </div>
          )}
          <Button
            onClick={refreshLink}
            disabled={submitting}
            className="bg-brand-green hover:bg-brand-green-dark text-white rounded-md"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
            Resume onboarding on Stripe
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/expert-dashboard")}
            className="mt-2 ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
          </Button>
        </div>
      </PageShell>
    );
  }

  // ─── State: not_started (default) ─────────────────────────────────────
  return (
    <PageShell>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-warm-grey-200 rounded-lg p-8"
      >
        <h2 className="font-display text-2xl mb-2">Set up payouts</h2>
        <p className="text-warm-grey-800 mb-6 leading-relaxed">
          To receive money from completed sessions, we need to verify your
          identity and connect a bank account. Stripe handles the whole flow —
          you'll be redirected to their hosted form, then back here.
        </p>

        <div className="mb-6">
          <Label htmlFor="country" className="block text-sm font-semibold mb-2">
            Which country are you in?
          </Label>
          <select
            id="country"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-full max-w-md border border-warm-grey-300 rounded-md px-3 py-2 bg-white text-warm-grey-900 focus:outline-none focus:ring-2 focus:ring-brand-green/30"
          >
            <option value="">— select a country —</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <p className="text-xs text-warm-grey-700 mt-2 max-w-md">
            Stripe Connect supports {countries.length} countries. If yours isn't
            listed, email{" "}
            <a href="mailto:founder@xpert.company" className="text-brand-green underline">
              founder@xpert.company
            </a>{" "}
            to arrange manual payouts.
          </p>
        </div>

        <div className="bg-paper border border-warm-grey-200 rounded p-4 text-sm text-warm-grey-800 leading-relaxed mb-6">
          <p className="mb-2"><strong>Before you start, know that:</strong></p>
          <ul className="space-y-1 list-disc pl-5">
            <li>Payouts are denominated in USD. If your bank account is in a non-USD currency, Stripe converts at its prevailing rate and may deduct an FX conversion fee.</li>
            <li>You'll need a government-issued ID, your tax info, and a bank account in the country you select above.</li>
            <li>The form usually takes 5-10 minutes. Stripe may take up to 48h to finish background verification.</li>
          </ul>
        </div>

        <Button
          onClick={startOnboarding}
          disabled={submitting || !selectedCountry}
          className="bg-brand-green hover:bg-brand-green-dark text-white rounded-md"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
          Continue to Stripe
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate("/expert-dashboard")}
          className="ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
        </Button>
      </motion.div>
    </PageShell>
  );
}


function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-paper">
      <MarketingNav />
      <main className="max-w-3xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}


/**
 * Stripe's requirements API returns machine-readable codes like
 * `individual.id_number` or `external_account`. Map the common ones to
 * human-readable labels; fall back to the raw code for anything we haven't
 * mapped yet (so the expert at least sees *something* and we don't silently
 * hide a missing requirement).
 */
function prettyRequirement(code) {
  const MAP = {
    "external_account":                 "Connect a bank account",
    "individual.id_number":             "Provide your tax ID (SSN / equivalent)",
    "individual.verification.document": "Upload a government-issued ID",
    "individual.verification.additional_document": "Upload an additional ID document",
    "individual.address.line1":         "Provide your address",
    "individual.dob.day":               "Provide your date of birth",
    "individual.dob.month":             "Provide your date of birth",
    "individual.dob.year":              "Provide your date of birth",
    "individual.first_name":            "Provide your first name",
    "individual.last_name":             "Provide your last name",
    "individual.email":                 "Confirm your email",
    "individual.phone":                 "Provide a phone number",
    "tos_acceptance.date":              "Accept the Stripe service agreement",
    "tos_acceptance.ip":                "Accept the Stripe service agreement",
    "business_profile.url":             "Provide a business URL (or LinkedIn/profile link)",
    "business_profile.mcc":             "Confirm your business category",
  };
  return MAP[code] || code;
}
